/**
 * Dinero POS en centavos (half-up). La pantalla usa las mismas reglas que el servidor;
 * el servidor sigue siendo la fuente de verdad del cobro.
 */

export function roundMoney(n) {
	return Math.round(Number(n) * 100) / 100;
}

/** Acepta 10.50 y 10,50 (caja en LATAM). */
export function parseMontoInput(raw) {
	let s = String(raw == null ? '' : raw).trim();
	if (!s) return 0;
	if (s.indexOf(',') >= 0 && s.indexOf('.') >= 0) {
		if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
			s = s.replace(/\./g, '').replace(',', '.');
		} else {
			s = s.replace(/,/g, '');
		}
	} else if (s.indexOf(',') >= 0) {
		s = s.replace(',', '.');
	}
	const n = parseFloat(s);
	return Number.isFinite(n) ? n : 0;
}

export function moneyToCents(n) {
	const x = Number(n);
	if (!Number.isFinite(x)) return 0;
	return Math.round(x * 100);
}

export function moneyFromCents(cents) {
	return (Number(cents) || 0) / 100;
}

export function moneyLineTotal(price, qty) {
	const q = Number(qty);
	if (!Number.isFinite(q) || q <= 0) return 0;
	return moneyFromCents(Math.round(moneyToCents(price) * q));
}

export function metodoEsEfectivo(metodo) {
	const m = String(metodo || '')
		.toLowerCase()
		.replace(/[_\s]/g, '-');
	return m.indexOf('efectivo') !== -1 || m === 'cash';
}

/**
 * Totales del carrito en USD/Bs. No lee el DOM.
 */
export function posknTotalesDesdeCarrito(win) {
	const carrito = win.carrito || [];
	const tasa = Number(win.TASA_DOLAR) || 1;
	const precioFn =
		typeof win.posknPrecioUnitarioCarrito === 'function'
			? win.posknPrecioUnitarioCarrito
			: (item) => {
					const usd = parseFloat(item.price) || 0;
					return { usd, bs: usd * tasa };
				};

	let subCents = 0;
	let subBsCents = 0;
	carrito.forEach((item) => {
		const u = precioFn(item);
		const qty = Number(item.cantidad) || 0;
		subCents += Math.round(moneyToCents(u.usd) * qty);
		subBsCents += Math.round(moneyToCents(u.bs) * qty);
	});

	const subtotalUsd = moneyFromCents(subCents);
	const subtotalBs = moneyFromCents(subBsCents);
	const descuentoUsd = roundMoney(
		typeof win.calcularDescuento === 'function' ? win.calcularDescuento(subtotalUsd) : 0,
	);
	const deliveryUsd = roundMoney(typeof win.getDeliveryFeeUsd === 'function' ? win.getDeliveryFeeUsd() : 0);
	const totalUsd = roundMoney(Math.max(0, subtotalUsd - descuentoUsd + deliveryUsd));
	const ratio = subtotalUsd > 0 ? (subtotalUsd - descuentoUsd) / subtotalUsd : 1;
	const descuentoBs = roundMoney(subtotalBs - subtotalBs * ratio);
	const deliveryBs = moneyFromCents(Math.round(moneyToCents(deliveryUsd) * tasa));
	const totalBs = roundMoney(Math.max(0, subtotalBs * ratio + deliveryBs));

	return {
		subtotalUsd,
		subtotalBs,
		descuentoUsd,
		descuentoBs,
		deliveryUsd,
		deliveryBs,
		totalUsd,
		totalBs,
		tasa,
	};
}

/**
 * Cubre el cobro si el USD o el Bs (o la mezcla) llega al total mostrado.
 * Evita el desfase de décimas al convertir Bs ↔ USD con tasa.
 */
export function posknResumenCobro(pagadoUsd, pagadoBs, totalUsd, totalBs, tasa) {
	const usdC = moneyToCents(pagadoUsd);
	const bsC = moneyToCents(pagadoBs);
	const totUsdC = moneyToCents(totalUsd);
	const totBsC = moneyToCents(totalBs);
	const t = Number(tasa);
	const tasaOk = Number.isFinite(t) && t > 0 ? t : 1;

	const bsComoUsdC = Math.round(bsC / tasaOk);
	const usdComoBsC = Math.round(usdC * tasaOk);
	const pagadoUsdEquivC = usdC + bsComoUsdC;
	const pagadoBsEquivC = bsC + usdComoBsC;
	const restUsdC = totUsdC - pagadoUsdEquivC;
	const restBsC = totBsC - pagadoBsEquivC;

	const cubierto =
		restUsdC <= 1 ||
		restBsC <= 1 ||
		(bsC === 0 && usdC >= totUsdC - 1) ||
		(usdC === 0 && bsC >= totBsC - 1);

	const vueltoUsd = restUsdC < -1;
	const vueltoBs = restBsC < -1;

	return {
		pagadoUsd: moneyFromCents(usdC),
		pagadoBs: moneyFromCents(bsC),
		restanteUsd: cubierto && !vueltoUsd ? 0 : moneyFromCents(restUsdC),
		restanteBs: cubierto && !vueltoBs ? 0 : moneyFromCents(restBsC),
		covered: cubierto,
		vueltoUsd,
		vueltoBs,
		restUsdC,
		restBsC,
		pagadoUsdEquivC,
		pagadoBsEquivC,
		totUsdC,
		totBsC,
		usdComoBsC,
		bsComoUsdC,
	};
}

export function posknBsAUsdCents(montoBs, tasa) {
	const t = Number(tasa);
	if (!Number.isFinite(t) || t <= 0) return 0;
	return Math.round(moneyToCents(montoBs) / t);
}

export function attachMoneyGlobals(win) {
	win.roundMoney = roundMoney;
	win.moneyToCents = moneyToCents;
	win.moneyFromCents = moneyFromCents;
	win.moneyLineTotal = moneyLineTotal;
	win.parseMontoInput = parseMontoInput;
	win.posknParseMontoInput = parseMontoInput;
	win.posknResumenCobro = posknResumenCobro;
	win.posknBsAUsdCents = posknBsAUsdCents;
	win.metodoEsEfectivo = metodoEsEfectivo;
	win.posknTotalesDesdeCarrito = () => posknTotalesDesdeCarrito(win);
}
